import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import MembersSidebar from '../components/MembersSidebar';

const MembersAccountDetails = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone_number: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadMemberData();
  }, []);

  const loadMemberData = () => {
    // Load from localStorage
    const memberData = localStorage.getItem('member_data');
    
    if (memberData) {
      try {
        const parsedMember = JSON.parse(memberData);
        setMember(parsedMember);
        setFormData({
          firstName: parsedMember.first_name || '',
          lastName: parsedMember.last_name || '',
          email: parsedMember.email || '',
          phone_number: parsedMember.phone_number || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } catch (error) {
        console.error('Error parsing member data:', error);
      }
    } else {
      // Redirect to login if no member data
      Swal.fire({
        icon: 'warning',
        title: 'Not Logged In',
        text: 'Please login to access account details.',
      }).then(() => {
        navigate('/members/members-area');
      });
    }
  };

  const handleLogout = () => {
    setLogoutLoading(true);
    
    // Clean up localStorage
    localStorage.removeItem('member_token');
    localStorage.removeItem('member_data');
    
    Swal.fire({
      icon: 'success',
      title: 'Signed Out',
      text: 'You have been signed out successfully.',
      timer: 2000,
      showConfirmButton: false,
    });
    
    navigate('/members/members-area');
    setLogoutLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone_number.trim()) newErrors.phone_number = 'Phone number is required';

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation - only if any password field is filled
    if (formData.currentPassword || formData.newPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Current password is required to change password';
      }
      if (formData.newPassword && formData.newPassword.length < 6) {
        newErrors.newPassword = 'New password must be at least 6 characters long';
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'New password and confirm password do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const memberData = JSON.parse(localStorage.getItem('member_data'));
      if (!memberData || !memberData.id) {
        throw new Error('Member data not found');
      }

      const memberId = memberData.id;
      const token = localStorage.getItem('member_token');
      const originalEmail = memberData.email; // Get original email
      
      // Prepare update data
      const updateData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone_number: formData.phone_number
      };

      // For password change - handle it separately
      let passwordChanged = false;
      if (formData.currentPassword && formData.newPassword) {
        // Try to login with ORIGINAL email and current password to verify it's correct
        try {
          const loginResponse = await axios.post('https://backend.dlapakistan.com/api/members/login/', {
            email: originalEmail, // Use original email, not the potentially changed one
            password: formData.currentPassword
          });

          if (loginResponse.data.success) {
            // Password is correct, include new password in update
            updateData.password = formData.newPassword;
            updateData.confirm_password = formData.confirmPassword;
            passwordChanged = true;
          } else {
            throw new Error('Current password is incorrect');
          }
        } catch (loginError) {
          setErrors(prev => ({
            ...prev,
            currentPassword: 'Current password is incorrect'
          }));
          throw new Error('Current password verification failed');
        }
      }

      // If token exists, use it. Otherwise, try without token
      const config = {};
      
      if (token) {
        config.headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
      } else {
        config.headers = {
          'Content-Type': 'application/json'
        };
      }

      let response;
      
      try {
        // First try with token
        response = await axios.put(
          `https://backend.dlapakistan.com/api/members/${memberId}/`,
          updateData,
          config
        );
      } catch (firstError) {
        if (firstError.response?.status === 401 && token) {
          // Token expired, try to get a new token
          try {
            // Try to login again to get fresh token
            const refreshLogin = await axios.post('https://backend.dlapakistan.com/api/members/login/', {
              email: originalEmail,
              password: formData.currentPassword || 'dummy' // Use current password or dummy
            });

            if (refreshLogin.data.success) {
              // Save new token
              const newToken = refreshLogin.data.access;
              localStorage.setItem('member_token', newToken);
              
              // Retry the update with new token
              response = await axios.put(
                `https://backend.dlapakistan.com/api/members/${memberId}/`,
                updateData,
                {
                  headers: {
                    'Authorization': `Bearer ${newToken}`,
                    'Content-Type': 'application/json'
                  }
                }
              );
            } else {
              throw firstError;
            }
          } catch (refreshError) {
            // If refresh fails, try without token
            response = await axios.put(
              `https://backend.dlapakistan.com/api/members/${memberId}/`,
              updateData,
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              }
            );
          }
        } else {
          throw firstError;
        }
      }

      if (response.data) {
        // Update local member data
        const updatedMember = {
          ...memberData,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone_number: formData.phone_number
        };

        // Save updated data to localStorage
        setMember(updatedMember);
        localStorage.setItem('member_data', JSON.stringify(updatedMember));

        // If password was changed and we got a new token, update it
        if (passwordChanged && response.config?.headers?.Authorization) {
          const newToken = response.config.headers.Authorization.replace('Bearer ', '');
          localStorage.setItem('member_token', newToken);
        }

        // Clear password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Account details updated successfully!',
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error('Error updating account:', error);
      
      let errorMessage = 'Failed to update account. Please try again.';
      
      if (error.response) {
        // Handle backend validation errors
        const backendErrors = error.response.data;
        
        if (backendErrors) {
          // Convert backend field names to frontend field names
          const fieldMapping = {
            'first_name': 'firstName',
            'last_name': 'lastName',
            'phone_number': 'phone_number',
            'password': 'newPassword',
            'confirm_password': 'confirmPassword'
          };

          const newErrors = {};
          
          // Check for email already exists
          if (backendErrors.email && 
              (backendErrors.email.includes('already exists') || 
               backendErrors.email.includes('unique'))) {
            newErrors.email = 'This email is already registered';
          }
          
          // Check for password mismatch
          if (backendErrors.password && backendErrors.password.includes('do not match')) {
            newErrors.confirmPassword = 'Passwords do not match';
          }

          // Handle all field errors
          Object.keys(backendErrors).forEach(key => {
            if (key !== 'non_field_errors' && fieldMapping[key]) {
              newErrors[fieldMapping[key]] = Array.isArray(backendErrors[key]) 
                ? backendErrors[key][0] 
                : backendErrors[key];
            }
          });

          // Handle non-field errors
          if (backendErrors.non_field_errors) {
            errorMessage = Array.isArray(backendErrors.non_field_errors)
              ? backendErrors.non_field_errors[0]
              : backendErrors.non_field_errors;
          }

          if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            errorMessage = 'Please fix the errors in the form';
          }
        }

        // Check for 404
        if (error.response.status === 404) {
          errorMessage = 'Member not found. Please login again.';
          localStorage.removeItem('member_token');
          localStorage.removeItem('member_data');
          navigate('/members/members-area');
        }
      } else if (error.message === 'Current password verification failed') {
        // Already handled in the password verification section
        errorMessage = 'Please check your current password';
      } else if (error.message.includes('Current password is incorrect')) {
        setErrors(prev => ({
          ...prev,
          currentPassword: 'Current password is incorrect'
        }));
        errorMessage = 'Current password is incorrect';
      }

      if (errorMessage !== 'Current password is incorrect') {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!member) {
    return (
      <Container fluid className="py-5 min-vh-100 mt-40" style={{ maxWidth: '95%' }}>
        <div className="text-center py-5">
          <p>Loading...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-5 min-vh-100 mt-40" style={{ maxWidth: '95%' }}>
      {/* Header with user info and logout */}
      <div className="mb-6">
        <h1 className="display-6 text-dark mb-3">
          Hello {member.first_name} {member.last_name}
        </h1>
        <p className="text-muted mb-3">
          Manage Account Details!{' '}{' '}{' '}
          <Button 
            variant="link" 
            onClick={handleLogout}
            disabled={logoutLoading}
            className="text-danger p-0"
            style={{ textDecoration: 'none' }}
          >
            {logoutLoading ? 'Signing out...' : 'Log out'}
          </Button>
        </p>
      </div>

      <Row className="g-4">
        {/* Sidebar Column */}
        <Col lg={3} md={4}>
          <MembersSidebar />
        </Col>

        {/* Content Column */}
        <Col lg={9} md={8}>
          <div className="bg-white shadow-sm rounded-lg p-4 p-lg-5">
            <div className="text-left">
              <h4 className="text-[#70B444] mb-4">Account Details</h4>
              
              <div className="mb-4">
                <p className="text-gray-700 mb-0">
                  Update your account information and password below.
                </p>
              </div>

              <Form onSubmit={handleSaveChanges} noValidate>
                {/* Personal Information Card */}
                <Card className="mb-4 border-0">
                  <Card.Body className="p-0">
                    <h5 className="text-dark mb-4">Personal Information</h5>
                    
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-medium">
                            First name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="First name"
                            required
                            isInvalid={!!errors.firstName}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.firstName}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-medium">
                            Last name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Last name"
                            required
                            isInvalid={!!errors.lastName}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.lastName}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-medium">
                            Email address <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Email address"
                            required
                            isInvalid={!!errors.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-4">
                          <Form.Label className="fw-medium">
                            Phone number <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="phone_number"
                            value={formData.phone_number}
                            onChange={handleInputChange}
                            placeholder="Phone number"
                            required
                            isInvalid={!!errors.phone_number}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.phone_number}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Password Change Card */}
                <Card className="mb-4 border-0">
                  <Card.Body className="p-0">
                    <h5 className="text-dark mb-4">Password Change</h5>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">
                        Current password (leave blank to keep current password)
                      </Form.Label>
                      <Form.Control
                        type="password"
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleInputChange}
                        placeholder="Current password"
                        isInvalid={!!errors.currentPassword}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.currentPassword}
                      </Form.Control.Feedback>
                      <div className="text-muted mt-1 small">
                        Required only if you want to change your password
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">
                        New password (leave blank to keep current password)
                      </Form.Label>
                      <Form.Control
                        type="password"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        placeholder="New password"
                        isInvalid={!!errors.newPassword}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.newPassword}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-medium">
                        Confirm new password
                      </Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirm new password"
                        isInvalid={!!errors.confirmPassword}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.confirmPassword}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Save Button */}
                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                  <div></div>
                  <Button 
                    variant="success" 
                    type="submit"
                    disabled={loading}
                    className="px-4"
                  >
                    {loading ? 'Saving...' : 'Save changes'}
                  </Button>
                </div>
              </Form>

              {/* Footer Information */}
              <p className="text-muted mb-0 mt-4">
                Select an option from the sidebar to manage other account settings.
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default MembersAccountDetails;